import get from 'lodash.get';
import isEmpty from 'lodash.isempty';

/* eslint-disable import/no-extraneous-dependencies */
import flatten from 'lodash/fp/flatten';
/* eslint-enable import/no-extraneous-dependencies */

import { INTERPRETATION_SHORT_NAME } from '../../constants/interpretations';
import { APPLICATIONS_GROUPS, DIBELS8, IDEL2, BURST } from '../../constants/applications';
import { BM_PERIODS, PERIOD } from '../../constants/enrollment';
import { MEASURE_NAMES } from '../../constants/assessments';

/**
 * Get interpretation's short name based on interpretation level
 * @param {string} interpretation interpretation's level
 * @returns {string} short name of interpretation
 */
export const getInterpsShortName = interpretation =>
    get(INTERPRETATION_SHORT_NAME, interpretation, '');

/**
 * Transforms interpretation's name to valid css class, e.g. "Well Below" --> "well-below"
 * @param {string} interpName interpretation's name to be transformed
 * @returns {string}
 */
export const transformInterpToCSSClass = interpName =>
    interpName
        .toLowerCase()
        .split(' ')
        .join('-');

/**
 * Get app statuses for the student by student's id grouped by TOY
 * @param {Object} appStatuses application statuses grouped by app name and period
 * @param {Array} appList array of applications to get the statuses for
 * @param {string} studentId student's id
 * @returns {Object} object with statuses for every app from the appList:
 *  {
 *      appName: {
 *          BOY: { ... },
 *          MOY: { ... },
 *          EOY: { ... },
 *      }
 *      ...
 *  }
 */
export const getAppStatusesForStudent = (appStatuses, appList, studentId) =>
    appList.reduce(
        (acc, appName) => ({
            ...acc,
            [appName]: Object.assign(
                {},
                ...Object.keys(PERIOD).map(period => ({
                    [period]: get(appStatuses, `[${appName}][${period}][${studentId}]`),
                })),
            ),
        }),
        {},
    );

/**
 * Get students list with application statuses for the selected app group
 * @param {Object} classesStudents object with students data grouped by class id
 * @param {string} appGroup applications group to get statuses for
 * @returns {Array} array with students data
 */
export const getStudentsListWithStatuses = (classesStudents, appGroup) => {
    const studentsList = get(classesStudents, `students`, []);
    const appStatuses = get(classesStudents, `appStatuses`, {});

    return studentsList.map(student => ({
        ...student,
        appStatuses: getAppStatusesForStudent(
            appStatuses,
            APPLICATIONS_GROUPS[appGroup],
            student.id,
        ),
    }));
};
/**
 * Get measure scores from result by its measure
 * @param {Array} results array with student's results
 * @param {string} measure measure name to get the scores for
 * @param {string} scoreComponent measure score name (LNF, NWF-CLS, ORF-ACC, etc)
 * @returns {Object} object with rawScore, interpretation and client resultId
 */
export const getMeasureScores = (results, measure, scoreComponent) => {
    const measureResult = results.find(result => result.measure === measure && result.isValid);
    if (!measureResult) {
        return undefined;
    }
    if (measure === MEASURE_NAMES.MAZE || measure === MEASURE_NAMES.CP) {
        return {
            rawScore: measureResult.rawScore,
            interpretation: measureResult.interpretation,
            dataSource: measureResult.dataSource,
        };
    }
    return (
        measureResult.measureScores[scoreComponent] && {
            ...measureResult.measureScores[scoreComponent],
            resultId: measureResult.clientId,
            formUid: measureResult.formUid,
            dataSource: measureResult.dataSource,
        }
    );
};

/**
 * This function is a handler for getStudentResults that filters
 * only valid student results
 * @param {Object} results contains object with students results
 * @param {string} appName name of current application
 * @param {string} period one of values: BOY, MOY, EOY, PM
 * @param {string} studentId current student id
 * @returns {Array<Object>} with valid student results
 */
export const getValidStudentResults = ({ results, appName, period, studentId }) =>
    get(results, `[${appName}][${period}][${studentId}]`, []).filter(result =>
        'isValid' in result ? result.isValid : true,
    );

/**
 * This function is a handler for getStudentResults getResults parameter
 * @param {Object} results contains object with students results
 * @param {string} appName name of current application
 * @param {string} period one of values: BOY, MOY, EOY, PM
 * @param {string} studentId current student id
 * @returns {Array<Object>} with all student results
 */
export const getAllStudentResults = ({ results, appName, period, studentId }) =>
    get(results, `[${appName}][${period}][${studentId}]`, []);

/**
 * Single out individual student's results from class students results pile.
 * Preserve APP->PERIOD->[RESULTS] grouping.
 * @param {Array<Object>} results map including all students results
 * @param {string} studentId student whose results are to be picked
 * @param {function} getResults function that filter student results
 * @returns {Array<Object>} single student's results by app and period
 */
export const getStudentResults = (results, studentId, getResults = getValidStudentResults) =>
    Object.keys(results).reduce((studentResults, appName) => {
        const studentApplicationResults = Object.values(PERIOD).reduce(
            (resultsByPeriod, period) => {
                return {
                    ...resultsByPeriod,
                    [period]: getResults({ results, appName, period, studentId }),
                };
            },
            {},
        );
        return {
            ...studentResults,
            [appName]: studentApplicationResults,
        };
    }, {});

/**
 * Merge student's PM results into a single array with no respect to source application.
 * @param {Object} studentResults student's results map: {<app name>: {<period> : [<results>] }}}
 * @returns {Array<Object>} array of all PM results
 */
export const getStudentPMResults = studentResults => {
    return Object.keys(studentResults).reduce((pmResults, appName) => {
        const appResults = get(studentResults, `[${appName}][${PERIOD.PM}]`, []);
        if (appName === DIBELS8) {
            pmResults.push(
                ...appResults.filter(
                    result => result.measure !== MEASURE_NAMES.MAZE && result.isValid,
                ),
            );
        } else if (appName === IDEL2) {
            pmResults.push(...appResults.filter(result => result.isValid));
        } else if (appName === BURST) {
            pmResults.push(...appResults);
        } else {
            pmResults.push(...appResults.filter(result => result.isComplete));
        }
        return pmResults;
    }, []);
};

/**
 * Merge student's Burst BM results into a single array with no respect to source application.
 * @param {Object} studentResults student's results map: {<app name>: {<period> : [<results>] }}}
 * @returns {Array<Object>} array of all Burst BM results
 */
export const getStudentBMBurstResults = studentResults =>
    BM_PERIODS.reduce((bmResults, period) => {
        const periodResults = get(studentResults, `[${BURST}][${period}]`, []);
        return [...bmResults, ...periodResults];
    }, []);

/**
 * Stack single student's results by application, i.e. set period grouping aside.
 * @param {Object} studentResults student's results map: {<app name>: {<period> : [<results>] }}}
 * @returns {<Object>} student results groupped by application: {<app name>: [<results>]}
 */
export const groupStudentResultsByApplication = studentResults => {
    return Object.keys(studentResults).reduce((studentResultsByApplication, applicationName) => {
        const studentApplicationResults = Object.values(PERIOD).reduce(
            (allPeriodsApplicationResults, period) => {
                return [
                    ...allPeriodsApplicationResults,
                    ...get(studentResults, `[${applicationName}][${period}]`, []),
                ];
            },
            [],
        );
        return {
            ...studentResultsByApplication,
            [applicationName]: studentApplicationResults,
        };
    }, {});
};

/**
 * Filter out invalid and unofficial BM and invalid PM burst results from results list.
 * @param {Array} studentResults - student results
 * @param {String} period
 * @returns {Array} list of official valid BM and valid PM results
 */
export const getValidOfficialBurstResults = (studentResults, period) => {
    const resultsByMeasureGenus = {};
    studentResults.forEach(result => {
        // accumulate all results for PM; for BM, official result overrides any unofficial result
        if (result.isValid) {
            if (!resultsByMeasureGenus[result.measureGenus]) {
                resultsByMeasureGenus[result.measureGenus] = [];
            }
            if (period === PERIOD.PM) {
                resultsByMeasureGenus[result.measureGenus].push(result);
            } else if (isEmpty(resultsByMeasureGenus[result.measureGenus]) || result.isOfficial) {
                resultsByMeasureGenus[result.measureGenus] = [result];
            }
        }
    });
    return flatten(Object.values(resultsByMeasureGenus));
};

/**
 * Return object with information for officialness popup
 * @param studentDetails
 * @param officialResult
 * @param unofficialResult
 * @returns {{ officialResultAssessorName: *,
 * officialResultDate: *, studentName: *, canReviewUnofficial: *, currentStaffResultDate: *}}
 */
export const getOfficialnessInfo = (studentDetails, officialResult, unofficialResult) => {
    // if official result was created not by current staff, we display No Result Details popup
    const officialnessInfo = {
        measure: MEASURE_NAMES.TRC,
        // name of another staff who created official result
        officialResultAssessorName: officialResult.staffName,
        // date when official result was created
        officialResultDate: officialResult.date,
        studentName: `${studentDetails.firstName} ${studentDetails.lastName}`,
        // so far this flag is false (default value), because unofficial result check is performed below
        canReviewUnofficial: false,
    };
    // but if there is some unofficial result created by current staff, we display Review Unofficial Result popup
    if (unofficialResult && !unofficialResult.isPaused) {
        // and now this flag is true
        officialnessInfo.canReviewUnofficial = true;
        // date when unofficial result was created
        officialnessInfo.currentStaffResultDate = unofficialResult.date;
    }

    return officialnessInfo;
};
